import { useCallback } from "react";
import { useIonAlert } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Address, TTaxiShippingQuote } from "@core/types";
import { shippingMethodsClient } from "@core/clients";
import { useOrderData } from "../../../providers/orderDataStore";
import { isTaxiQuoteExpired } from "../../../providers/address/taxiMasterShippingUtils";

const formatWarningLines = (quote: TTaxiShippingQuote, t: (key: string) => string): string => {
  if (!quote.warnings.length) {
    return "";
  }

  const lines = quote.warnings.map((warning) => {
    const { message, delayMin, delayMax } = warning;

    if (delayMin != null && delayMax != null) {
      return `- ${t("deliveryWarningWithDelayRange")
        .replace("{{message}}", message)
        .replace("{{min}}", String(delayMin))
        .replace("{{max}}", String(delayMax))}`;
    }

    return `- ${message}`;
  });

  return `\n\n${t("deliveryWarningsTitle")}:\n${lines.join("\n")}`;
};

export const useConfirmTaxiPriceChange = () => {
  const { t } = useTranslation();
  const [presentAlert] = useIonAlert();
  const { shippingMethod, taxiQuote, updateOrderData } = useOrderData();

  const confirmTaxiPriceChange = useCallback(
    async (address: Address | null): Promise<boolean> => {
      if (!shippingMethod || shippingMethod.methodType !== "taxi_master" || !address) {
        return true;
      }

      const previousTotal = taxiQuote?.total ?? (shippingMethod.cost ? Number(shippingMethod.cost) : null);
      const shouldRecalculate = !taxiQuote || isTaxiQuoteExpired(taxiQuote);

      let nextQuote = taxiQuote;

      if (shouldRecalculate) {
        try {
          nextQuote = await shippingMethodsClient.calculateTaxiShipping(address);
        } catch (error) {
          toast.error(error instanceof Error ? error.message : t("deliveryRouteNotFound"));
          return false;
        }

        if (!nextQuote?.total) {
          toast.error(t("deliveryRouteNotFound"));
          return false;
        }
      }

      if (!nextQuote?.total) {
        toast.error(t("Taxi delivery cost not calculated"));
        return false;
      }

      if (previousTotal != null && previousTotal !== nextQuote.total) {
        const confirmed = await new Promise<boolean>((resolve) => {
          presentAlert({
            backdropDismiss: false,
            keyboardClose: false,
            header: t("deliveryPriceChangedTitle"),
            message: [
              t("deliveryPriceChangedDescription"),
              t("deliveryPricePrevious", { total: previousTotal }),
              t("deliveryPriceCurrent", { total: nextQuote!.total }),
              formatWarningLines(nextQuote!, t),
            ]
              .filter(Boolean)
              .join("\n"),
            buttons: [
              {
                text: t("orderConfirmationCancel"),
                role: "cancel",
                handler: () => resolve(false),
              },
              {
                text: t("deliveryPriceConfirm"),
                role: "confirm",
                handler: () => resolve(true),
              },
            ],
          });
        });

        if (!confirmed) {
          updateOrderData({
            shippingMethod: { ...shippingMethod, cost: nextQuote.total!.toString() },
            taxiQuote: nextQuote,
          });
          return false;
        }
      }

      updateOrderData({
        shippingMethod: { ...shippingMethod, cost: nextQuote.total!.toString() },
        taxiQuote: nextQuote,
      });

      return true;
    },
    [presentAlert, shippingMethod, t, taxiQuote, updateOrderData],
  );

  return { confirmTaxiPriceChange };
};
